import type { BreedingInventorySnapshot, PairView, TankView } from "./liveBreedingInventory";

export type PairMoveValidationCode =
  | "PAIR_NOT_ACTIVE"
  | "PAIR_OCCUPANCY_MISSING"
  | "PAIR_DESTINATION_REQUIRED"
  | "ALREADY_IN_TARGET_TANK"
  | "TARGET_TANK_INVALID"
  | "TARGET_TANK_OCCUPIED";

export type PairMoveValidationResult =
  | { ok: true; pair: PairView; sourceTank: TankView; targetTank: TankView }
  | { ok: false; code: PairMoveValidationCode };

export function pairCurrentTank(
  snapshot: BreedingInventorySnapshot,
  pairId: string,
): TankView | undefined {
  const occupancy = snapshot.occupancies.find(
    (item) => item.occupantKind === "pair" && item.pairId === pairId,
  );
  return occupancy
    ? snapshot.tanks.find((tank) => tank.id === occupancy.tankId && tank.active)
    : undefined;
}

export function pairHasActiveCycle(snapshot: BreedingInventorySnapshot, pairId: string): boolean {
  return snapshot.cycles.some(
    (cycle) => cycle.pairId === pairId && cycle.state !== "completed" && cycle.state !== "failed",
  );
}

export function eligiblePairMoveDestinations(
  snapshot: BreedingInventorySnapshot,
  pairId: string,
): TankView[] {
  const sourceTank = pairCurrentTank(snapshot, pairId);

  return snapshot.tanks.filter(
    (tank) =>
      tank.active &&
      tank.id !== sourceTank?.id &&
      !snapshot.occupancies.some((occupancy) => occupancy.tankId === tank.id),
  );
}

export function validatePairMoveSelection(
  snapshot: BreedingInventorySnapshot,
  pairId: string,
  targetTankId: string,
): PairMoveValidationResult {
  const pair = snapshot.pairs.find((item) => item.id === pairId && item.status === "active");
  if (!pair) return { ok: false, code: "PAIR_NOT_ACTIVE" };

  const sourceTank = pairCurrentTank(snapshot, pairId);
  if (!sourceTank) return { ok: false, code: "PAIR_OCCUPANCY_MISSING" };

  if (!targetTankId) return { ok: false, code: "PAIR_DESTINATION_REQUIRED" };
  if (targetTankId === sourceTank.id) {
    return { ok: false, code: "ALREADY_IN_TARGET_TANK" };
  }

  const targetTank = snapshot.tanks.find((tank) => tank.id === targetTankId && tank.active);
  if (!targetTank) return { ok: false, code: "TARGET_TANK_INVALID" };

  if (snapshot.occupancies.some((occupancy) => occupancy.tankId === targetTankId)) {
    return { ok: false, code: "TARGET_TANK_OCCUPIED" };
  }

  return { ok: true, pair, sourceTank, targetTank };
}

export function buildPairMoveSummary(input: {
  pairCode: string;
  sourceTankCode: string;
  targetTankCode: string;
}): string[] {
  return [
    `جفت: ${input.pairCode}`,
    `از تانک: ${input.sourceTankCode}`,
    `به تانک: ${input.targetTankCode}`,
    "هویت جفت و سابقه تکثیر حفظ می‌شود",
  ];
}
